/**
 * GuidedJavaPackagingView — Build / Package stage (Modernization Journey)
 *
 * Receives svcGenJobIds from Guided Service Generation and packages all
 * fan-out outputs into a single deployable Spring Boot application.
 *
 * Journey-only path. Does NOT touch classic Workflow JPkg behavior.
 *
 * Backend flow:
 *   POST /journey-java-packaging/start
 *     → consolidates N SvcGen S3 prefixes → queues standard java_packaging job
 *   Poll GET /jobs/{job_id}       (generic poller — same as all other nodes)
 *   Download GET /journey-java-packaging/download/{job_id}  (S3-aware)
 *   Artifacts GET /journey-java-packaging/artifact/{job_id}/{filename}
 *
 * View states: 'pre-run' | 'running' | 'fetching' | 'success' | 'failure'
 */

import { useState, useRef, useEffect } from 'react'
import {
  ArrowLeft, ChevronRight, Settings, HelpCircle, User, Home,
  Workflow, Plug, FileBarChart, BarChart2, LayoutGrid,
  ChevronDown, CheckCircle2, Loader2, AlertTriangle, AlertCircle,
  Package, MoveRight, Archive, Download, XCircle, FileText,
} from 'lucide-react'
import { getAPIBaseUrl, isQA } from '../../environment'
import { pollJobUntilComplete } from '../../utils/jobPoller'
import { getAuthHeaders } from '../../services/nodeExecutors/authHelper'
import './GuidedJavaPackagingView.css'
import '../runs/RunsView.css'

// ── Named phases ──────────────────────────────────────────────────────────────
const PHASES = [
  { key: 'validate', label: 'Java Validation', threshold: 25  },
  { key: 'compile',  label: 'Maven Compile',   threshold: 75  },
  { key: 'bundle',   label: 'Bundle Assembly', threshold: 100 },
]

function getActivePhaseIndex(pct) {
  if (pct < 25) return 0
  if (pct < 75) return 1
  return 2
}

// ── Artifact metadata ─────────────────────────────────────────────────────────
const ARTIFACT_META = {
  'java_package_bundle': {
    filename: 'java_package_bundle.zip',
    label: 'Spring Boot Package',
    desc: 'Deployable application ZIP',
    isZip: true,
  },
  'validation_report': {
    filename: 'validation_report.json',
    label: 'Validation Report',
    desc: 'Pre-flight & compile results',
    isZip: false,
  },
  'build_manifest': {
    filename: 'build_manifest.json',
    label: 'Build Manifest',
    desc: 'Full file catalog',
    isZip: false,
  },
  'package_statistics': {
    filename: 'package_statistics.json',
    label: 'Package Statistics',
    desc: 'Build metrics',
    isZip: false,
  },
  'build_instructions': {
    filename: 'build_instructions.json',
    label: 'Build Instructions',
    desc: 'Configuration & commands',
    isZip: false,
  },
  'generated_java_validation_report': {
    filename: 'generated_java_validation_report.json',
    label: 'Java Validation Report',
    desc: 'Pre-compile detail',
    isZip: false,
  },
  // project_bundle is the uncompiled scaffolded Maven project uploaded on every failure.
  // It exists at {job_s3_prefix}project_bundle.zip — downloadable via /download/{job_id}
  // which falls back to project_bundle.zip when java_package_bundle.zip is not found.
  'project_bundle': {
    filename: 'project_bundle.zip',
    label: 'Generated Output',
    desc: 'Scaffolded Maven project (uncompiled)',
    isZip: true,
  },
}

const ARTIFACT_ORDER = [
  'java_package_bundle',
  'validation_report',
  'build_manifest',
  'package_statistics',
  'build_instructions',
  'generated_java_validation_report',
]

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

// ─────────────────────────────────────────────────────────────────────────────

export default function GuidedJavaPackagingView({
  project,
  svcGenJobIds = [],
  refineCodeJobId = null,    // Optional: Refine Code job ID to thread into JPkg start
  preFlightJobId = null,     // Optional: Pre-Flight job ID for pre-assembly check
  onBack,
  onBackToProjects,
  onGoToAnalyze,
  onGoToDecomposeCode,
  onGoToServiceGeneration,
  onGoToGenerateDocs,
  onGoToExtractBizLogic,
  onGoToRefineCode,          // Navigate to Refine Code stage
  onGoToRefactorCode,        // Navigate to Refactor Code stage
  onOpenSettings,
  onOpenHelp,
  onOpenReports,
  userProfile = {},
}) {
  const projectName = project?.name || 'Unknown Project'
  const accountId   = project?.account || ''
  const apiBase     = getAPIBaseUrl()

  // ── View state ─────────────────────────────────────────────────────────────
  // 'pre-run' | 'running' | 'fetching' | 'success' | 'failure'
  const [viewState,    setViewState]    = useState('pre-run')
  const [progress,     setProgress]    = useState(0)
  const [progressMsg,  setProgressMsg] = useState('')
  const [runError,     setRunError]    = useState(null)
  const [jpkgJobId,    setJpkgJobId]   = useState(null)
  const [jobData,      setJobData]     = useState(null)      // output_data from GET /jobs/{id}
  const [artifactList, setArtifactList]= useState([])        // produced_artifacts
  const abortRef = useRef(null)

  // ── Pre-assembly check state ───────────────────────────────────────────────
  const [preCheck,        setPreCheck]        = useState(null)   // null=not loaded, false=error, object=data
  const [preCheckLoading, setPreCheckLoading] = useState(false)

  // Trigger pre-assembly check when entering pre-run state with SvcGen job IDs
  useEffect(() => {
    if (viewState !== 'pre-run' || svcGenJobIds.length === 0) return
    setPreCheckLoading(true)
    setPreCheck(null)
    const params = new URLSearchParams()
    params.set('account_id', accountId)
    params.set('application_name', projectName)
    svcGenJobIds.forEach(id => params.append('svcgen_job_id', id))
    if (preFlightJobId) params.set('pre_flight_job_id', preFlightJobId)
    if (refineCodeJobId) params.set('refine_code_job_id', refineCodeJobId)
    fetch(`${apiBase}/journey-java-packaging/pre-assembly-check?${params}`, { headers: getAuthHeaders() })
      .then(r => r.ok ? r.json() : Promise.reject(r.status))
      .then(data => { setPreCheck(data); setPreCheckLoading(false) })
      .catch(() => { setPreCheck(false); setPreCheckLoading(false) })
  }, [viewState, svcGenJobIds, preFlightJobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── UI state ───────────────────────────────────────────────────────────────
  const [showMoreMenu, setShowMoreMenu] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)

  const getShortName = () => {
    const name = userProfile?.displayName || userProfile?.email || ''
    return name.split(' ')[0] || name || 'User'
  }

  // ── Fetch job details after polling completes ──────────────────────────────
  const fetchJobDetails = async (jobId) => {
    setViewState('fetching')
    try {
      const headers = getAuthHeaders({})
      const [jobRes, artRes] = await Promise.all([
        fetch(`${apiBase}/jobs/${encodeURIComponent(jobId)}`, { headers }),
        fetch(`${apiBase}/jobs/${encodeURIComponent(jobId)}/artifacts`, { headers }),
      ])

      let outputData = {}
      let jobStatus  = ''
      if (jobRes.ok) {
        const jobJson  = await jobRes.json()
        outputData     = jobJson.output_data || {}
        jobStatus      = jobJson.status || ''
      }

      let artifacts = []
      if (artRes.ok) {
        const artJson = await artRes.json()
        artifacts     = artJson.produced_artifacts || []
      }

      setJobData(outputData)
      setArtifactList(artifacts)

      // The jobs API normalises DB status to lowercase ('failed'), but the executor
      // writes output_data.status = 'FAILED' (uppercase).  Check both to be safe.
      const jobStatusUpper = jobStatus.toUpperCase()
      const outputStatus   = (outputData.status || '').toUpperCase()
      const compileStatus  = (outputData.compile_status || outputData.compile?.compile_status || '').toUpperCase()
      const isFailed = (
        ['FAILED', 'ERROR'].includes(jobStatusUpper) ||
        outputStatus === 'FAILED' ||
        ['FAILED', 'TIMEOUT', 'ERROR'].includes(compileStatus)
      )
      setViewState(isFailed ? 'failure' : 'success')
    } catch (_err) {
      // If fetching job details fails and we already have a run error, stay on failure.
      // Otherwise fall through to success so the user can still attempt a download.
      setViewState('failure')
    }
  }

  // ── Run Build / Package ────────────────────────────────────────────────────
  const handleRun = async () => {
    if (viewState === 'running') return

    let userId = ''
    try {
      userId = JSON.parse(localStorage.getItem('modernizeit_user') || '{}').user_id || ''
    } catch (_) {}

    const controller = new AbortController()
    abortRef.current = controller

    setViewState('running')
    setRunError(null)
    setProgress(0)
    setProgressMsg('Consolidating service outputs…')
    setJobData(null)
    setArtifactList([])

    try {
      setProgress(5)
      setProgressMsg('Starting Build / Package…')

      const startBody = {
        scout_account_id: accountId,
        application_name: projectName,
        user_id:          userId,
        svcgen_job_ids:   svcGenJobIds,
      }
      // Thread Refine Code job ID when coming from Refine Code → JPkg handoff
      if (refineCodeJobId) {
        startBody.refine_code_job_id = refineCodeJobId
      }

      const startRes = await fetch(`${apiBase}/journey-java-packaging/start`, {
        method: 'POST',
        headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(startBody),
        signal: controller.signal,
      })

      if (!startRes.ok) {
        const err = await startRes.json().catch(() => ({ detail: `HTTP ${startRes.status}` }))
        throw new Error(err.detail || `API error: ${startRes.status}`)
      }

      const startData = await startRes.json()
      const jobId = startData.job_id
      if (!jobId) throw new Error('API did not return a job_id')

      setJpkgJobId(jobId)
      setProgress(10)
      setProgressMsg('Build job queued, waiting for worker…')

      await pollJobUntilComplete(jobId, apiBase, {
        maxWaitSeconds: 900,
        pollInterval:   3000,
        onProgress: (pct, msg) => {
          const mapped = 10 + (pct * 0.88)
          setProgress(mapped)
          setProgressMsg(msg || 'Packaging…')
        },
        signal: controller.signal,
      })

      setProgress(100)
      setProgressMsg('Build / Package complete')
      await fetchJobDetails(jobId)

    } catch (err) {
      if (err.message === 'Execution cancelled') {
        setViewState('pre-run')
      } else if (err.message?.startsWith('Job failed:')) {
        // The job completed but in a FAILED state.  The poller throws here, which means
        // fetchJobDetails was never called and jobData is still null.
        // Call fetchJobDetails now so the failure screen gets output_data (artifacts_path,
        // project_bundle_uri, validation reports) and can offer download buttons.
        setRunError(err.message)
        try {
          await fetchJobDetails(jobId)
        } catch (_fetchErr) {
          // If detail fetch also errors, at least show the failure screen with whatever
          // error message we have.
          setViewState('failure')
        }
      } else {
        // Network error, timeout, unexpected exception — no job output to fetch.
        setRunError(err.message || 'Build / Package failed')
        setViewState('failure')
      }
    } finally {
      abortRef.current = null
    }
  }

  // ── Download individual artifact ───────────────────────────────────────────
  const handleDownloadArtifact = async (artifactName) => {
    const meta = ARTIFACT_META[artifactName]
    if (!meta || !jpkgJobId) return
    const token = localStorage.getItem('modernizeit_token') || ''
    const headers = token ? { Authorization: `Bearer ${token}` } : {}
    try {
      const url = meta.isZip
        ? `${apiBase}/journey-java-packaging/download/${encodeURIComponent(jpkgJobId)}`
        : `${apiBase}/journey-java-packaging/artifact/${encodeURIComponent(jpkgJobId)}/${encodeURIComponent(meta.filename)}`
      const res = await fetch(url, { headers })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const blob   = await res.blob()
      const objUrl = URL.createObjectURL(blob)
      const a      = document.createElement('a')
      a.href       = objUrl
      a.download   = meta.isZip ? `ModernizedApplication_${projectName}.zip` : meta.filename
      document.body.appendChild(a)
      a.click()
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(objUrl) }, 500)
    } catch (err) {
      setRunError(`Download failed: ${err.message}`)
    }
  }

  // ── Compile status helper ──────────────────────────────────────────────────
  const getCompileStatus = () => {
    const cs = (jobData?.compile_status || '').toUpperCase()
    if (cs === 'PASSED')
      return { label: 'Passed',                cls: 'gjpv-cs-passed',  icon: <CheckCircle2 size={11} /> }
    if (cs === 'WARNING')
      return { label: 'Passed with warnings',  cls: 'gjpv-cs-warning', icon: <AlertTriangle size={11} /> }
    if (['FAILED', 'TIMEOUT', 'ERROR'].includes(cs))
      return { label: cs === 'TIMEOUT' ? 'Timed out' : 'Failed', cls: 'gjpv-cs-failed', icon: <XCircle size={11} /> }
    return { label: cs || '—', cls: 'gjpv-cs-muted', icon: null }
  }

  // ── Reset to pre-run ───────────────────────────────────────────────────────
  const resetToPreRun = () => {
    setViewState('pre-run')
    setJobData(null)
    setArtifactList([])
    setRunError(null)
    setProgress(0)
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Nav header
  // ─────────────────────────────────────────────────────────────────────────
  const renderNavHeader = () => (
    <div className="runs-header-bar">
      <div className="runs-header-left">
        <button className="runs-back-btn" onClick={onBack}>
          <ArrowLeft size={15} /><span>Runs</span>
        </button>
        <div className="runs-breadcrumb">
          <span className="runs-bc-project">{projectName}</span>
          <ChevronRight size={14} className="runs-bc-sep" />
          <span className="runs-bc-page">Build / Package</span>
        </div>
      </div>

      <div className="runs-header-center">
        <div className="mode-switcher">
          <button className="mode-btn" onClick={onBackToProjects || onBack} title="Projects">
            <Home size={16} /><span>Home</span>
          </button>
          <button className="mode-btn" onClick={() => onGoToAnalyze?.()} title="Analyze">
            <BarChart2 size={16} /><span>Analyze</span>
          </button>
          {/* Workflow and MCP hidden — accessible via Journey dropdown
          <button className="mode-btn" disabled title="Workflow">
            <Workflow size={16} /><span>Workflow</span>
          </button>
          <button className="mode-btn" disabled title="MCP — coming soon">
            <Plug size={16} /><span>MCP</span>
          </button>
          */}
          <button className="mode-btn" onClick={() => onOpenReports?.()} title="Reports">
            <FileBarChart size={16} /><span>Reports</span>
          </button>

          <div className="more-menu-container">
            <button
              className={`mode-btn more-trigger ${showMoreMenu ? 'active' : ''}`}
              onClick={() => setShowMoreMenu(v => !v)}
              disabled={false}
              title="HITL Journey"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="4" y1="6" x2="20" y2="6" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="18" x2="20" y2="18" />
              </svg>
              <ChevronDown size={12} className={`more-chevron ${showMoreMenu ? 'open' : ''}`} />
            </button>

            {showMoreMenu && (
              <>
                <div className="more-backdrop" onClick={() => setShowMoreMenu(false)} />
                <div className="more-dropdown">
                  <div className="dropdown-section-label">MODERNIZATION FLOW</div>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToAnalyze?.() }}>
                    <span>Analyze</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToGenerateDocs?.() }}>
                    <span>Generate Documentation</span>
                  </button>
                  {/* EBL disabled — graph + AIL replaces it (see docs/20260721_define_services_architecture.md)
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToExtractBizLogic?.() }}>
                    <span>Extract Business Logic</span>
                  </button>
                  */}
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToDecomposeCode?.() }}>
                    <span>Define Services</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToServiceGeneration?.() }}>
                    <span>Build Services</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefactorCode?.() }}>
                    <span>Refactor Code</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onGoToRefineCode?.() }}>
                    <span>Refine Code</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item active-item" onClick={() => setShowMoreMenu(false)}>
                    <span>Build / Package</span>
                  </button>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenReports?.() }}>
                    <span>Reports</span>
                  </button>
                  <button className="dropdown-item" onClick={() => { setShowMoreMenu(false); onOpenSettings?.() }}>
                    <span>Settings</span>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="runs-header-right">
        <button className="icon-btn" onClick={onOpenSettings} title="Settings">
          <Settings size={18} />
        </button>
        <button className="icon-btn" onClick={onOpenHelp} title="Help">
          <HelpCircle size={18} />
        </button>
        <div className="user-menu-container">
          <button className="user-btn" onClick={() => setShowUserMenu(v => !v)}>
            <div className="user-avatar"><User size={16} /></div>
            <span>{getShortName()}</span>
            <ChevronDown size={14} />
          </button>
          {showUserMenu && (
            <div className="user-dropdown">
              <div className="dropdown-header">
                <span className="user-name">{userProfile.displayName || 'User'}</span>
                <span className="user-email">{userProfile.email || ''}</span>
              </div>
              <div className="dropdown-divider" />
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onOpenSettings?.() }}>
                <Settings size={16} /><span>Settings</span>
              </button>
              <button className="dropdown-item" onClick={() => { setShowUserMenu(false); onBack?.() }}>
                <Home size={16} /><span>Back to Runs</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  // ─────────────────────────────────────────────────────────────────────────
  // Pre-Assembly Check panel
  // ─────────────────────────────────────────────────────────────────────────
  const renderPreAssemblyCheck = () => {
    if (preCheckLoading) {
      return (
        <div className="gjpv-pac-panel">
          <div className="gjpv-pac-header">
            <span className="gjpv-pac-title">Pre-Assembly Check</span>
            <Loader2 size={13} className="gjpv-spin gjpv-pac-spinner" />
          </div>
          <div className="gjpv-pac-skeleton">
            <div className="gjpv-pac-skel-row" />
            <div className="gjpv-pac-skel-row gjpv-pac-skel-short" />
          </div>
        </div>
      )
    }

    if (preCheck === false) {
      return (
        <div className="gjpv-pac-panel gjpv-pac-panel-warn">
          <div className="gjpv-pac-header">
            <span className="gjpv-pac-title">Pre-Assembly Check</span>
            <AlertTriangle size={13} className="gjpv-pac-warn-ico" />
          </div>
          <p className="gjpv-pac-warn-msg">
            Could not load pre-assembly data. Build / Package is still available.
          </p>
        </div>
      )
    }

    if (!preCheck) return null

    const counts = preCheck.counts || {}
    const pf     = preCheck.pre_flight
    const risks  = preCheck.risks || []

    // Pre-flight badge
    let pfBadge = null
    if (!pf) {
      pfBadge = <span className="gjpv-pac-pf-badge gjpv-pac-pf-none">Not Run ○</span>
    } else {
      const pfs = (pf.status || '').toLowerCase()
      if (pfs === 'clean' || pfs === 'passed' || pfs === 'completed') {
        pfBadge = <span className="gjpv-pac-pf-badge gjpv-pac-pf-clean">Clean ✓</span>
      } else if (pf.error_count != null && pf.error_count > 0) {
        pfBadge = <span className="gjpv-pac-pf-badge gjpv-pac-pf-errors">Errors ⚠ {pf.error_count}</span>
      } else {
        pfBadge = <span className="gjpv-pac-pf-badge gjpv-pac-pf-none">Not Run ○</span>
      }
    }

    const hasAmberRed = risks.some(r => r.level === 'amber' || r.level === 'red')
    const checkedAt   = preCheck.checked_at
      ? new Date(preCheck.checked_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : null

    return (
      <div className="gjpv-pac-panel">
        <div className="gjpv-pac-header">
          <span className="gjpv-pac-title">Pre-Assembly Check</span>
          {checkedAt && <span className="gjpv-pac-ts">{checkedAt}</span>}
        </div>

        {/* Counts chips */}
        <div className="gjpv-pac-counts">
          {[
            { key: 'services',     label: 'Services'     },
            { key: 'interfaces',   label: 'Interfaces'   },
            { key: 'entities',     label: 'Entities'     },
            { key: 'repositories', label: 'Repos'        },
            { key: 'shared',       label: 'Shared'       },
          ].map(({ key, label }) => (
            <div key={key} className="gjpv-pac-count-chip">
              <span className="gjpv-pac-count-num">{counts[key] ?? '—'}</span>
              <span className="gjpv-pac-count-lbl">{label}</span>
            </div>
          ))}
        </div>

        {/* Pre-flight row */}
        <div className="gjpv-pac-pf-row">
          <span className="gjpv-pac-pf-label">Pre-Flight</span>
          {pfBadge}
          {pf && pf.error_count != null && (
            <span className="gjpv-pac-pf-errcnt">{pf.error_count} error{pf.error_count !== 1 ? 's' : ''}</span>
          )}
        </div>

        {/* Risks */}
        {risks.length > 0 && (
          <div className="gjpv-pac-risks">
            {risks.map((r, i) => (
              <div
                key={i}
                className={`gjpv-pac-risk gjpv-pac-risk-${r.level}`}
              >
                <span className="gjpv-pac-risk-title">{r.title}</span>
                <span className="gjpv-pac-risk-detail">{r.detail}</span>
              </div>
            ))}
          </div>
        )}

        {hasAmberRed && (
          <p className="gjpv-pac-caveat">
            Build / Package will still run but validation may fail.
          </p>
        )}
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Stage header
  // ─────────────────────────────────────────────────────────────────────────
  const renderStageHeader = () => {
    let badge
    if (viewState === 'success') {
      badge = <span className="gjpv-badge gjpv-badge-success"><CheckCircle2 size={11} />Complete</span>
    } else if (viewState === 'failure') {
      badge = <span className="gjpv-badge gjpv-badge-failed"><XCircle size={11} />Failed</span>
    } else if (viewState === 'running' || viewState === 'fetching') {
      badge = <span className="gjpv-badge gjpv-badge-running"><Loader2 size={11} className="gjpv-spin" />Running</span>
    } else if (svcGenJobIds.length === 0) {
      badge = <span className="gjpv-badge gjpv-badge-needs-input"><AlertTriangle size={11} />Needs Input</span>
    } else {
      badge = <span className="gjpv-badge gjpv-badge-ready"><Archive size={11} />Ready to Package</span>
    }

    return (
      <div className="gjpv-stage-header">
        <div className="gjpv-stage-left">
          <div className="gjpv-stage-title-row">
            <h2 className="gjpv-stage-title">Build / Package</h2>
            <span className="gjpv-stage-set-name">{projectName} · Modernization Journey</span>
          </div>
          <div className="gjpv-stage-chips">
            <span className="gjpv-chip gjpv-chip-done">Analyze ✓</span>
            <span className="gjpv-chip-sep">›</span>
            <span className="gjpv-chip gjpv-chip-done">Define Services ✓</span>
            <span className="gjpv-chip-sep">›</span>
            <span className="gjpv-chip gjpv-chip-done">Build Services ✓</span>
            <span className="gjpv-chip-sep">›</span>
            <span className="gjpv-chip gjpv-chip-active">Build / Package</span>
          </div>
        </div>
        <div className="gjpv-stage-right">{badge}</div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // No-input state  (svcGenJobIds is empty)
  // ─────────────────────────────────────────────────────────────────────────
  const renderNoInput = () => (
    <div className="gjpv-no-input">
      {/* Explanation panel */}
      <div className="gjpv-no-input-header">
        <div className="gjpv-no-input-icon-wrap">
          <Package size={22} />
        </div>
        <div className="gjpv-no-input-body">
          <span className="gjpv-no-input-title">Build Services output required</span>
          <span className="gjpv-no-input-sub">
            Build / Package consolidates generated Java services into one production-ready Spring
            Boot application. At least one completed Build Services run is needed before packaging
            can begin.
          </span>
        </div>
      </div>

      {/* What this step does */}
      <div className="gjpv-what-section">
        <span className="gjpv-result-section-title">What this step does</span>
        <div className="gjpv-what-list">
          <div className="gjpv-what-item">
            <CheckCircle2 size={13} className="gjpv-what-ico" />
            <span>Validates and compiles all generated Java service files</span>
          </div>
          <div className="gjpv-what-item">
            <CheckCircle2 size={13} className="gjpv-what-ico" />
            <span>
              Assembles a Spring Boot application with Maven build, Docker configuration, and
              REST controllers
            </span>
          </div>
          <div className="gjpv-what-item">
            <CheckCircle2 size={13} className="gjpv-what-ico" />
            <span>Produces a downloadable application ZIP and build reports</span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="gjpv-no-input-actions">
        <button className="gjpv-btn gjpv-btn-primary" onClick={() => onGoToServiceGeneration?.()}>
          <MoveRight size={13} />Go to Build Services
        </button>
        <button className="gjpv-btn gjpv-btn-secondary" onClick={() => onGoToDecomposeCode?.()}>
          <ArrowLeft size={13} />Back to Define Services
        </button>
      </div>
    </div>
  )

  // ─────────────────────────────────────────────────────────────────────────
  // Pre-run state  (svcGenJobIds present — ready to run)
  // ─────────────────────────────────────────────────────────────────────────
  const renderPreRun = () => {
    if (svcGenJobIds.length === 0) return renderNoInput()

    return (
    <div className="gjpv-ready">
      <p className="gjpv-step-desc">
        Build / Package assembles all generated services into one production-ready Spring Boot
        application with Maven build, Docker configuration, REST controllers, and a deployable
        ZIP package. Review the inputs below, then run packaging.
      </p>

      <div className="gjpv-input-card">
        <div className="gjpv-input-card-hdr">
          <Archive size={14} />
          <span>Inputs from Build Services</span>
        </div>
        <div className="gjpv-input-body">
          <div className="gjpv-input-stat">
            <span className="gjpv-stat-num">{svcGenJobIds.length || '—'}</span>
            <span className="gjpv-stat-lbl">
              Build Services job{svcGenJobIds.length !== 1 ? 's' : ''} to package
            </span>
          </div>
          {svcGenJobIds.length > 0 && (
            <div className="gjpv-job-list">
              {svcGenJobIds.map((jid, i) => (
                <div key={jid} className="gjpv-job-row">
                  <span className="gjpv-job-idx">Job {i + 1}</span>
                  <code className="gjpv-job-id">{jid}</code>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Refine Code upstream indicator */}
      {refineCodeJobId && (
        <div className="gjpv-input-card" style={{ marginTop: 10 }}>
          <div className="gjpv-input-card-hdr">
            <CheckCircle2 size={14} />
            <span>Refine Code output will be included</span>
          </div>
          <div className="gjpv-input-body">
            <div className="gjpv-input-stat">
              <span className="gjpv-stat-lbl" style={{ fontSize: 12 }}>
                Refine Code job: <code style={{ fontSize: '0.9em', opacity: 0.8 }}>{refineCodeJobId}</code>
              </span>
            </div>
          </div>
        </div>
      )}

      {renderPreAssemblyCheck()}

      {runError && (
        <div className="gjpv-error-banner">
          <AlertCircle size={14} />
          <span>{runError}</span>
        </div>
      )}

      <div className="gjpv-run-row">
        <button
          className="gjpv-btn gjpv-btn-primary"
          onClick={handleRun}
          disabled={preCheckLoading}
          title={preCheckLoading ? 'Waiting for Pre-Assembly Check to complete…' : undefined}
        >
          <Package size={14} />{preCheckLoading ? 'Checking readiness…' : 'Run Build / Package'}
        </button>
        <button
          className="gjpv-btn gjpv-btn-secondary"
          onClick={() => onGoToServiceGeneration?.()}
        >
          <ArrowLeft size={13} />Back to Build Services
        </button>
      </div>
    </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Running state
  // ─────────────────────────────────────────────────────────────────────────
  const renderRunning = () => {
    const activePhase = getActivePhaseIndex(progress)
    return (
      <div className="gjpv-running">
        <div className="gjpv-running-header">
          <Loader2 size={18} className="gjpv-spin" />
          <span className="gjpv-running-title">Build / Package in progress</span>
        </div>

        <div className="gjpv-phase-list">
          {PHASES.map((ph, i) => {
            const isDone   = i < activePhase
            const isActive = i === activePhase
            return (
              <div
                key={ph.key}
                className={`gjpv-phase-row ${isDone ? 'gjpv-phase-done' : isActive ? 'gjpv-phase-active' : 'gjpv-phase-pending'}`}
              >
                <div className="gjpv-phase-dot">
                  {isDone   && <CheckCircle2 size={14} />}
                  {isActive && <Loader2 size={14} className="gjpv-spin" />}
                  {!isDone && !isActive && <span className="gjpv-phase-idx">{i + 1}</span>}
                </div>
                <span className="gjpv-phase-label">{ph.label}</span>
              </div>
            )
          })}
        </div>

        <div className="gjpv-progress">
          <div className="gjpv-progress-label">
            <span>{progressMsg || 'Running…'}</span>
            <span className="gjpv-progress-pct">{Math.round(progress)}%</span>
          </div>
          <div className="gjpv-progress-track">
            <div className="gjpv-progress-fill" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Fetching state
  // ─────────────────────────────────────────────────────────────────────────
  const renderFetching = () => (
    <div className="gjpv-fetching">
      <Loader2 size={20} className="gjpv-spin" />
      <span>Loading results…</span>
    </div>
  )

  // ─────────────────────────────────────────────────────────────────────────
  // Success state
  // ─────────────────────────────────────────────────────────────────────────
  const renderSuccess = () => {
    const cs = getCompileStatus()
    // The java_packaging_executor writes metrics under output_data.statistics.*
    // (not at the top level of output_data). Reading the nested path gives the
    // actual count from the packaging run; the old top-level fallback was always
    // undefined and caused the count to fall back to svcGenJobIds.length (number
    // of SvcGen jobs = number of COBOL programs, not logical services).
    const stats            = jobData?.statistics || {}
    const servicesPackaged = stats.services_packaged ?? svcGenJobIds.length
    const totalFiles       = stats.total_files ?? null
    const totalSize        = stats.package_size_bytes ?? null

    // ── Readiness metadata (from _compute_package_readiness) ──────────────────
    const readiness          = stats.package_readiness ?? 'compiled_project'
    const runnableJar        = stats.runnable_jar_present ?? false
    const smokeStatus        = stats.smoke_check_status ?? 'not_run'
    const entityMissingId    = stats.entity_missing_id_count ?? 0
    const entityMissingFiles = stats.entity_missing_id_files ?? []
    const stubCount          = stats.stub_service_count ?? 0
    const stubFiles          = stats.stub_service_files ?? []

    // ── Four-tier readiness copy ───────────────────────────────────────────────
    const READINESS_CONFIG = {
      deployable: {
        sub:        'Deployable Spring Boot application package',
        badgeLabel: 'Deployable',
        badgeCls:   'gjpv-readiness-deployable',
        bannerCls:  '',
      },
      packaged_with_warnings: {
        sub:        'Runnable JAR included — runtime readiness warnings found',
        badgeLabel: 'Packaged with Warnings',
        badgeCls:   'gjpv-readiness-warn',
        bannerCls:  'gjpv-result-banner-warning',
      },
      compiled_project: {
        sub:        'Compiled Spring Boot project package',
        badgeLabel: 'Compiled Project',
        badgeCls:   'gjpv-readiness-compiled',
        bannerCls:  '',
      },
      validation_failed: {
        sub:        'Packaging validation failed — review warnings before proceeding',
        badgeLabel: 'Validation Issues',
        badgeCls:   'gjpv-readiness-failed',
        bannerCls:  'gjpv-result-banner-warning',
      },
    }
    const rc = READINESS_CONFIG[readiness] ?? READINESS_CONFIG.compiled_project

    // ── Warnings list ─────────────────────────────────────────────────────────
    const warnings = []
    if (!runnableJar) {
      warnings.push({
        key: 'no-jar',
        icon: '📦',
        text: 'Runnable JAR not found in target/ — the ZIP contains source only. Run mvn package inside the extracted project to produce the JAR.',
      })
    }
    if (entityMissingId > 0) {
      const affected = entityMissingFiles.length
        ? ` Affected: ${entityMissingFiles.slice(0, 3).join(', ')}${entityMissingFiles.length > 3 ? ` +${entityMissingFiles.length - 3} more` : ''}.`
        : ''
      warnings.push({
        key: 'missing-id',
        icon: '🔑',
        text: `${entityMissingId} JPA ${entityMissingId === 1 ? 'entity is' : 'entities are'} missing @Id — Hibernate will fail to boot without a primary key annotation.${affected}`,
      })
    }
    if (smokeStatus === 'failed') {
      warnings.push({
        key: 'smoke-failed',
        icon: '🚨',
        text: 'Smoke check failed — the application did not start successfully during packaging validation.',
      })
    } else if (smokeStatus === 'not_run') {
      warnings.push({
        key: 'smoke-not-run',
        icon: '⚠️',
        text: 'Smoke check not run — runtime startup was not verified during this build.',
      })
    }
    if (stubCount > 0) {
      const stubNames = stubFiles.length
        ? ` (${stubFiles.slice(0, 3).join(', ')}${stubFiles.length > 3 ? ` +${stubFiles.length - 3} more` : ''})`
        : ''
      warnings.push({
        key: 'stubs',
        icon: '🔧',
        text: `${stubCount} Default*Service stub ${stubCount === 1 ? 'class' : 'classes'} present — placeholder implementations that return empty data until real business logic is wired in${stubNames}.`,
      })
    }

    // Show artifacts in preferred order; always show the main ZIP even if not in artifact list
    const presentArtifacts = ARTIFACT_ORDER.filter(name =>
      name === 'java_package_bundle' || artifactList.some(a => a.name === name)
    )

    return (
      <div className="gjpv-result">
        {/* Result banner */}
        <div className={`gjpv-result-banner ${rc.bannerCls}`}>
          <CheckCircle2 size={20} className="gjpv-result-icon" />
          <div className="gjpv-result-banner-text">
            <span className="gjpv-result-title">Build / Package Complete</span>
            <span className="gjpv-result-sub">{rc.sub}</span>
          </div>
          <span className={`gjpv-readiness-badge ${rc.badgeCls}`}>{rc.badgeLabel}</span>
        </div>

        {/* Warnings (only shown when present) */}
        {warnings.length > 0 && (
          <div className="gjpv-readiness-warnings">
            <span className="gjpv-result-section-title">Warnings ({warnings.length})</span>
            <div className="gjpv-readiness-warning-list">
              {warnings.map(w => (
                <div key={w.key} className="gjpv-readiness-warning-item">
                  <span className="gjpv-readiness-warning-icon">{w.icon}</span>
                  <span className="gjpv-readiness-warning-text">{w.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Metrics */}
        <div className="gjpv-metrics-row">
          <div className="gjpv-metric-card">
            <span className="gjpv-metric-num">{servicesPackaged}</span>
            <span className="gjpv-metric-lbl">Services packaged</span>
          </div>
          {totalFiles !== null && (
            <div className="gjpv-metric-card">
              <span className="gjpv-metric-num">{totalFiles}</span>
              <span className="gjpv-metric-lbl">Total files</span>
            </div>
          )}
          {totalSize !== null && (
            <div className="gjpv-metric-card">
              <span className="gjpv-metric-num">{formatBytes(totalSize)}</span>
              <span className="gjpv-metric-lbl">Package size</span>
            </div>
          )}
          {jpkgJobId && (
            <div className="gjpv-metric-card gjpv-metric-card-wide">
              <span className="gjpv-metric-num gjpv-metric-jobid">{jpkgJobId.slice(0, 8)}…</span>
              <span className="gjpv-metric-lbl">Job ID</span>
            </div>
          )}
        </div>

        {/* Artifact downloads */}
        <div className="gjpv-artifact-section">
          <span className="gjpv-result-section-title">Artifacts</span>
          <div className="gjpv-artifact-list">
            {presentArtifacts.map(name => {
              const meta = ARTIFACT_META[name]
              if (!meta) return null
              return (
                <div key={name} className="gjpv-artifact-row">
                  <div className="gjpv-artifact-info">
                    <FileText size={14} className="gjpv-artifact-ico" />
                    <div className="gjpv-artifact-text">
                      <span className="gjpv-artifact-label">{meta.label}</span>
                      <span className="gjpv-artifact-desc">{meta.desc}</span>
                    </div>
                  </div>
                  <button
                    className={`gjpv-btn ${meta.isZip ? 'gjpv-btn-download' : 'gjpv-btn-secondary'}`}
                    onClick={() => handleDownloadArtifact(name)}
                  >
                    <Download size={12} />
                    {meta.isZip ? 'Download' : 'Export'}
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        {/* Next steps */}
        <div className="gjpv-result-next-steps">
          <span className="gjpv-result-section-title">Next steps</span>
          <div className="gjpv-result-next-list">
            <div className="gjpv-result-next-item">
              <MoveRight size={11} className="gjpv-result-next-ico" />
              <span>Extract the ZIP and run <code>mvn clean package</code> or <code>docker-compose up --build</code></span>
            </div>
            <div className="gjpv-result-next-item">
              <MoveRight size={11} className="gjpv-result-next-ico" />
              <span>Review generated REST controllers and wire any missing business logic</span>
            </div>
            <div className="gjpv-result-next-item">
              <MoveRight size={11} className="gjpv-result-next-ico" />
              <span>Run the pre-flight validator before integrating into your build pipeline</span>
            </div>
          </div>
        </div>

        {/* Download error (only shows after a failed download attempt) */}
        {runError && (
          <div className="gjpv-error-banner">
            <AlertCircle size={14} />
            <span>{runError}</span>
          </div>
        )}

        {/* Re-run */}
        <div className="gjpv-result-actions">
          <button className="gjpv-btn gjpv-btn-secondary" onClick={resetToPreRun}>
            Re-run Packaging
          </button>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Failure state
  // ─────────────────────────────────────────────────────────────────────────
  const renderFailure = () => {
    const compileError = jobData?.compile_error || jobData?.error || runError || 'Build / Package did not complete successfully.'

    // ── Determine what output actually exists for this failed job ─────────────
    //
    // On failure, the executor uploads partial artifacts to S3 and writes
    // output_data.artifacts_path + output_data.project_bundle_uri into the job record.
    // The produced_artifacts manifest is NOT written on failure, so artifactList is
    // always empty for failed jobs.  We must check jobData fields directly.
    //
    // Priority for download bundle:
    //   1. java_package_bundle in artifactList  → succeeded but landed here (edge case)
    //   2. project_bundle_uri in jobData        → executor uploaded project_bundle.zip on failure
    //   3. artifacts_path in jobData            → partial artifacts prefix exists (implies project_bundle.zip too)
    //
    const hasSuccessBundle  = artifactList.some(a => a.name === 'java_package_bundle')
    const hasProjectBundle  = !!(jobData?.project_bundle_uri || jobData?.artifacts_path)
    const hasAnyBundle      = hasSuccessBundle || hasProjectBundle
    // Which artifact name drives the download call:
    const bundleArtifactName = hasSuccessBundle ? 'java_package_bundle' : 'project_bundle'

    // validation_report.json is ALWAYS uploaded by _upload_partial_failure_artifacts
    // when artifacts_path is set — we don't need it in artifactList to offer the download.
    const hasValidationReport = (
      artifactList.some(a => a.name === 'validation_report') ||
      !!jobData?.artifacts_path
    )
    const hasGenJavaReport = (
      artifactList.some(a => a.name === 'generated_java_validation_report')
    )

    // Build the legacy reportArtifacts list (used by the existing artifact rows section)
    const reportArtifacts = [
      hasValidationReport && 'validation_report',
      hasGenJavaReport    && 'generated_java_validation_report',
    ].filter(Boolean)

    return (
      <div className="gjpv-failure">
        {/* ── Error banner ── */}
        <div className="gjpv-error-banner gjpv-error-banner-lg">
          <AlertCircle size={18} className="gjpv-error-ico" />
          <div>
            <span className="gjpv-error-title">Build / Package Failed</span>
            <span className="gjpv-error-detail">{compileError}</span>
          </div>
        </div>

        {/* ── Package / output availability notice ── */}
        {hasAnyBundle ? (
          <div className="gjpv-pkg-available-notice">
            <Archive size={14} className="gjpv-pkg-available-ico" />
            <div className="gjpv-pkg-available-text">
              <span className="gjpv-pkg-available-title">
                {hasSuccessBundle ? 'Package available for download' : 'Generated output available for download'}
              </span>
              <span className="gjpv-pkg-available-body">
                Build validation failed, but generated output is available for download.
                Review the validation report to resolve compile or profile issues before deploying.
              </span>
            </div>
          </div>
        ) : (
          jpkgJobId && (
            <div className="gjpv-pkg-unavailable-notice">
              Generated output is not available for this failed build.
            </div>
          )
        )}

        {/* ── Fix guidance ── */}
        <div className="gjpv-fix-guidance">
          <span className="gjpv-result-section-title">Fix Guidance</span>
          <div className="gjpv-fix-list">
            <div className="gjpv-fix-item">
              <span className="gjpv-fix-num">1</span>
              <div>
                <span className="gjpv-fix-heading">Review the Validation Report</span>
                <span className="gjpv-fix-body">
                  Download the validation report to see which Java files failed pre-flight checks or compilation.
                </span>
              </div>
            </div>
            <div className="gjpv-fix-item">
              <span className="gjpv-fix-num">2</span>
              <div>
                <span className="gjpv-fix-heading">Return to Build Services</span>
                <span className="gjpv-fix-body">
                  Edit the generated Java source in Build Services, fix any compile errors, and re-run Build / Package.
                </span>
              </div>
            </div>
            <div className="gjpv-fix-item">
              <span className="gjpv-fix-num">3</span>
              <div>
                <span className="gjpv-fix-heading">Check Service Generation inputs</span>
                <span className="gjpv-fix-body">
                  If multiple services are failing, the issue may originate in the COBOL-to-Java translation step.
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Report downloads (validation reports — always shown when present) ── */}
        {reportArtifacts.length > 0 && (
          <div className="gjpv-artifact-section">
            <span className="gjpv-result-section-title">Available Reports</span>
            <div className="gjpv-artifact-list">
              {reportArtifacts.map(name => {
                const meta = ARTIFACT_META[name]
                if (!meta) return null
                return (
                  <div key={name} className="gjpv-artifact-row">
                    <div className="gjpv-artifact-info">
                      <FileText size={14} className="gjpv-artifact-ico" />
                      <div className="gjpv-artifact-text">
                        <span className="gjpv-artifact-label">{meta.label}</span>
                        <span className="gjpv-artifact-desc">{meta.desc}</span>
                      </div>
                    </div>
                    <button
                      className="gjpv-btn gjpv-btn-secondary"
                      onClick={() => handleDownloadArtifact(name)}
                    >
                      <Download size={12} />Export
                    </button>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* ── Actions ── */}
        <div className="gjpv-run-row">
          {hasAnyBundle && (
            <button
              className="gjpv-btn gjpv-btn-download"
              onClick={() => handleDownloadArtifact(bundleArtifactName)}
            >
              <Archive size={14} />
              {hasSuccessBundle ? 'Download Package' : 'Download Generated Output'}
            </button>
          )}
          {hasValidationReport && (
            <button
              className="gjpv-btn gjpv-btn-secondary"
              onClick={() => handleDownloadArtifact('validation_report')}
            >
              <Download size={12} />Download Validation Report
            </button>
          )}
          <button className="gjpv-btn gjpv-btn-primary" onClick={handleRun}>
            <Package size={14} />Re-run Build / Package
          </button>
          <button className="gjpv-btn gjpv-btn-secondary" onClick={() => onGoToServiceGeneration?.()}>
            <ArrowLeft size={13} />Back to Build Services
          </button>
        </div>
      </div>
    )
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Root
  // ─────────────────────────────────────────────────────────────────────────
  const renderContent = () => {
    switch (viewState) {
      case 'running':  return renderRunning()
      case 'fetching': return renderFetching()
      case 'success':  return renderSuccess()
      case 'failure':  return renderFailure()
      default:         return renderPreRun()
    }
  }

  return (
    <div className="gjpv-root">
      {renderNavHeader()}
      {renderStageHeader()}
      <div className="gjpv-body">
        {renderContent()}
      </div>
    </div>
  )
}
